import useSWR from 'swr';
import { useCallback, useState } from 'react';
import {
  agentService,
  promptService,
  toolService,
  modelService,
  quickstartService,
  isApiError,
} from '@dopomogai/api-client';
import type {
  AgentResponse,
  PromptResponse,
  ToolResponse,
  AvailableLLM,
  UUID,
  QuickstartCreatePayload,
  QuickstartResponse,
} from '@dopomogai/types';
import { AgentKitError } from '../types';

/**
 * The data required to populate an agent creation form.
 */
interface CreateAgentData {
  availableModels: AvailableLLM[];
  availablePrompts: PromptResponse[];
  availableTools: ToolResponse[];
}

/**
 * The payload for the `createAgent` function. It describes the desired
 * configuration for the new agent, abstracting away the underlying API calls.
 */
export interface CreateAgentPayload {
  name: string;
  llmModel: string;
  description?: string;
  promptOption:
    | { type: 'none' }
    | { type: 'existing'; id: UUID }
    | { type: 'new'; name: string; content: string };
  toolSlugs?: string[];
}

export interface QuickstartAgentPayload {
  agentName: string;
  promptContent: string;
  websiteDomain: string;
  widgetConfig?: Record<string, any>;
  notifyEmails?: string[];
}

export type QuickstartAgentResult = QuickstartResponse;

const CREATION_DATA_CACHE_KEY = 'dopomogai/agents/creation-data';

const shouldFallbackToLegacyQuickstart = (errorLike: any): boolean => {
  const status = errorLike?.status ?? errorLike?.response?.status ?? errorLike?.data?.status;
  if (status === 404 || status === 405) return true;

  const message = String(
    errorLike?.message ||
      errorLike?.data?.detail ||
      errorLike?.response?.data?.detail ||
      ''
  ).toLowerCase();

  return (
    message.includes('agent-widget-v2') && message.includes('not found')
  );
};

const creationDataFetcher = async (): Promise<CreateAgentData> => {
  const [modelsResponse, promptsResponse, toolsResponse] = await Promise.all([
    modelService.getAvailableModels(),
    // Fetch only prompts that are not already linked to an agent.
    promptService.getAllPrompts({ unlinked_only: true }), 
    toolService.getAllTools(),
  ]);

  if (isApiError(modelsResponse)) throw new AgentKitError('Failed to fetch available models.', modelsResponse);
  if (isApiError(promptsResponse)) throw new AgentKitError('Failed to fetch available prompts.', promptsResponse);
  if (isApiError(toolsResponse)) throw new AgentKitError('Failed to fetch available tools.', toolsResponse);

  return {
    availableModels: modelsResponse,
    availablePrompts: promptsResponse,
    availableTools: toolsResponse,
  };
};

/**
 * A hook to facilitate the creation of new agents.
 * It provides all the necessary data for a creation form (models, prompts, tools)
 * and a single function to handle the creation logic.
 */
export const useCreateAgent = () => {
  // Fetch all the "ingredients" for the creation form.
  const { data, error: dataError, isLoading: dataIsLoading } = useSWR<CreateAgentData, AgentKitError>(
    CREATION_DATA_CACHE_KEY,
    creationDataFetcher
  );
  
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState<AgentKitError | null>(null);

  const [isQuickStarting, setIsQuickStarting] = useState(false);
  const [quickstartError, setQuickstartError] = useState<AgentKitError | null>(null);

  /**
   * Orchestrates the creation of an agent and its related resources (like a new prompt).
   * This simplifies the UI logic into a single function call.
   * @param payload The configuration for the new agent.
   * @returns The newly created AgentResponse.
   */
  const createAgent = useCallback(async (payload: CreateAgentPayload): Promise<AgentResponse> => {
    setIsCreating(true);
    setCreationError(null);

    try {
      // Step 1: Create the base agent with tools attached.
      const agentPayload = {
        name: payload.name,
        llm_model: payload.llmModel,
        description: payload.description,
        tool_ids: payload.toolSlugs,
      };
      const newAgent = await agentService.createAgent(agentPayload);
      if (isApiError(newAgent)) {
        throw new AgentKitError('Failed to create the agent.', newAgent);
      }

      // Step 2: Handle the prompt based on the user's choice.
      switch (payload.promptOption.type) {
        case 'existing':
          // Link and activate an existing prompt.
          const linkResult = await agentService.setActivePromptForAgent(newAgent.id, payload.promptOption.id);
          if (isApiError(linkResult)) {
            console.warn(`Agent created (ID: ${newAgent.id}), but failed to link existing prompt.`, linkResult);
            // Decide if this is a critical failure. For now, we'll let it pass but warn.
          }
          break;
        
        case 'new':
          // Create a new prompt, then link and activate it.
          const newPrompt = await promptService.createPrompt({
              name: payload.promptOption.name,
              content: payload.promptOption.content,
          });
          if (isApiError(newPrompt)) {
              throw new AgentKitError(`Agent created (ID: ${newAgent.id}), but failed to create the new prompt.`, newPrompt);
          }
          
          const newLinkResult = await agentService.setActivePromptForAgent(newAgent.id, newPrompt.id);
          if (isApiError(newLinkResult)) {
              throw new AgentKitError(`Agent and prompt created, but failed to link them.`, newLinkResult);
          }
          break;

        case 'none':
        default:
          // No prompt action needed.
          break;
      }
      
      // Return the fully created agent.
      // We refetch the agent to get its final state with the active_prompt_id.
      const finalAgentState = await agentService.getAgent(newAgent.id);
      if (isApiError(finalAgentState)) {
          console.warn("Could not refetch final agent state, returning initial state.");
          return newAgent;
      }
      return finalAgentState;

    } catch (err: any) {
      const wrappedError = err instanceof AgentKitError ? err : new AgentKitError('An unexpected error occurred during agent creation.', err);
      setCreationError(wrappedError);
      throw wrappedError; // Re-throw for the calling component to handle.
    } finally {
      setIsCreating(false);
    }
  }, []);

  /**
   * Quickstart flow: one-call creation of agent + prompt + widget template/version.
   */
  const createAgentQuickstart = useCallback(
    async (payload: QuickstartAgentPayload): Promise<QuickstartAgentResult> => {
      setIsQuickStarting(true);
      setQuickstartError(null);

      const request: QuickstartCreatePayload = {
        agent_name: payload.agentName,
        prompt_content: payload.promptContent,
        website_domain: payload.websiteDomain,
        widget_config: payload.widgetConfig,
        notify_emails: payload.notifyEmails,
      };

      try {
        const v2Response = await quickstartService.createAgentWidgetV2(request);
        if (!isApiError(v2Response)) {
          return v2Response;
        }

        if (!shouldFallbackToLegacyQuickstart(v2Response)) {
          throw new AgentKitError('Failed to run quickstart creation (v2).', v2Response);
        }

        const legacyResponse = await quickstartService.createAgentWidget(request);
        if (isApiError(legacyResponse)) {
          throw new AgentKitError('Failed to run quickstart creation.', legacyResponse);
        }
        return legacyResponse;
      } catch (err: any) {
        if (shouldFallbackToLegacyQuickstart(err)) {
          const legacyResponse = await quickstartService.createAgentWidget(request);
          if (isApiError(legacyResponse)) {
            const wrappedLegacyError = new AgentKitError('Quickstart creation failed.', legacyResponse);
            setQuickstartError(wrappedLegacyError);
            throw wrappedLegacyError;
          }
          return legacyResponse;
        }
        const wrappedError = err instanceof AgentKitError ? err : new AgentKitError('Quickstart creation failed.', err);
        setQuickstartError(wrappedError);
        throw wrappedError;
      } finally {
        setIsQuickStarting(false);
      }
    },
    []
  );

  return {
    // Data for the form
    availableModels: data?.availableModels,
    availablePrompts: data?.availablePrompts,
    availableTools: data?.availableTools,
    isLoading: dataIsLoading,
    error: dataError,
    // State and function for the creation action
    isCreating,
    creationError,
    createAgent,
    // Quickstart
    isQuickStarting,
    quickstartError,
    createAgentQuickstart,
  };
};
